import React, { useEffect, useState } from 'react';
import '../App.css';
import '../css/UsuariosAdmin.css';
import { request } from '../utils/ApiUtils';
import { showNotification } from '../utils/NotificationUtils';
import { rolesMapping } from '../utils/RolesUtils';
import { getUserId } from '../utils/TokenUtils';
import Table from './Table';
import { useNavigate } from 'react-router-dom';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import { FaUserShield } from 'react-icons/fa';

const roleNames = [
  'contratos', 'clientes', 'colaboradores', 'comissao', 'admin',
  'configdespesas', 'balancofinanceiro', 'despesas'
];

const roleLabels = {
  contratos: 'Contratos',
  clientes: 'Clientes',
  colaboradores: 'Colaboradores',
  comissao: 'Comissão',
  admin: 'Admin',
  configdespesas: 'Config. despesas',
  balancofinanceiro: 'Balanço financeiro',
  despesas: 'Despesas'
};

function UsuariosAdmin() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [userToDelete, setUserToDelete] = useState(null);
  const currentUserId = getUserId();

  const loadUsers = () => {
    setLoading(true);
    request('admin/usuarios', {
      type: 'GET',
      callback: (data) => setUsers(data || []),
      callbackError: (_, message) => showNotification(message || 'Não foi possível carregar os usuários.', 'error'),
      finally: () => setLoading(false)
    });
  };

  useEffect(() => { loadUsers(); }, []);

  const isAssigned = (user, roleName) =>
    rolesMapping[roleName]
      .every((technicalRole) => user.roles?.includes(technicalRole));

  const canDelete = (user) => user.id !== currentUserId && !isAssigned(user, 'admin');

  const deleteUser = () => {
    if (!userToDelete) return;

    setSaving((current) => ({ ...current, [userToDelete.id]: true }));
    request(`admin/usuarios/${userToDelete.id}`, {
      type: 'DELETE',
      callback: () => {
        setUsers((current) => current.filter((user) => user.id !== userToDelete.id));
        showNotification('Usuário excluído.', 'success');
      },
      callbackError: (_, message) => showNotification(message || 'Não foi possível excluir o usuário.', 'error'),
      finally: () => {
        setSaving((current) => ({ ...current, [userToDelete.id]: false }));
        setUserToDelete(null);
      }
    });
  };

  const tableParams = {
    containerClassName: 'usuarios-admin-table',
    columns: [
      {
        label: 'Usuário',
        width: '15%',
        type: 'input',
        key: 'username',
        getValue: (user) => user.username
      },
      {
        label: 'E-mail',
        width: '20%',
        type: 'input',
        key: 'email',
        getValue: (user) => user.email || '-'
      },
      {
        label: 'Permissões',
        width: '50%',
        type: 'input',
        key: 'roles',
        getValue: (user) => roleNames.filter((roleName) => isAssigned(user, roleName)).map((roleName) => roleLabels[roleName]).join(', ') || '-'
      },
      {
        label: 'Visualizar',
        width: '8%',
        type: 'button-icon',
        css: 'button',
        title: 'Editar usuário',
        icon: <VisibilityIcon />,
        onClick: (user) => navigate(`/admin/usuarios/cadastro/${user.id}`)
      },
      {
        label: 'Excluir',
        width: '7%',
        type: 'button-icon',
        css: 'usuarios-admin-delete-button',
        title: 'Excluir usuário',
        icon: <DeleteIcon />,
        color: (user) => canDelete(user) ? '#c62828' : '#757575',
        disabled: (user) => !canDelete(user) || Boolean(saving[user.id]),
        onClick: (user) => setUserToDelete(user)
      }
    ]
  };

  return (
    <div className="form-container list-page">
      <div id="notification-container" className="notification-container usuarios-admin-notifications" />
      <div className="header-container">
        <div className="page-title-content">
          <FaUserShield aria-hidden="true" />
          <h1 style={{ color: 'black' }}>Usuários e permissões</h1>
        </div>
      </div>
      <Table params={tableParams} dataArray={users} loading={{ loading }} />
      {userToDelete && (
        <div className="usuarios-admin-modal-overlay" role="dialog" aria-modal="true" aria-label="Confirmar exclusão">
          <div className="usuarios-admin-modal">
            <h3>Excluir usuário</h3>
            <p>Deseja excluir o usuário <strong>{userToDelete.username}</strong>?</p>
            <div className="usuarios-admin-modal-actions">
              <button type="button" onClick={deleteUser}>Sim</button>
              <button type="button" onClick={() => setUserToDelete(null)}>Não</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UsuariosAdmin;
